import { useMemo } from "react";
import s from "./mobile-class-board.module.scss";
import classNames from "classnames";
import { isToday, format, isBefore } from "date-fns";
import { ru } from "date-fns/locale";
import { TZDate } from "@date-fns/tz";
import { queryClient } from "@shared/api/query-client";
import { useScheduleModal } from "@features/schedule/schedule-modal";
import { LessonItem } from "./lesson-item";
import { TimezoneButton } from "@features/profile/timezone";
import { EditScheduleModal } from "@features/teacher/edit-schedule";

export const MobileClassBoard = ({
  appointments = [],
  isTeacher,
  className,
}) => {
  const { data: profile } = queryClient?.getQueryData(['profile']) || {};
  const { open: openScheduleModal } = useScheduleModal();

  // Проверяем наличие домашнего задания
  const hasHomework = useMemo(() => {
    return appointments?.some(
      (apt) => isTeacher ? apt.has_unreviewed_homework : apt.has_new_homework
    );
  }, [appointments, isTeacher]);

  // Группируем занятия по датам
  const groupedAppointments = useMemo(() => {
    if (!appointments?.length) return { today: [], laterThisMonth: [], nextMonth: [] };

    const todayInTz = new TZDate(new Date(), profile?.timezone);
    todayInTz.setHours(0, 0, 0, 0);
    const nextMonth = new TZDate(todayInTz.getFullYear(), todayInTz.getMonth() + 1, 1, profile?.timezone);

    const grouped = {
      today: [],
      laterThisMonth: [],
      nextMonth: [],
    };

    [...appointments]
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
      .forEach((apt) => {
        const aptDate = new TZDate(apt.starts_at, profile?.timezone);
        aptDate.setHours(0, 0, 0, 0);

        const isTodayDate = isToday(aptDate);

        if (isTodayDate) {
          grouped.today.push(apt);
        } else if (!isBefore(aptDate, todayInTz) && isBefore(aptDate, nextMonth)) {
          // Только будущие занятия текущего месяца (без прошедших)
          grouped.laterThisMonth.push(apt);
        } else if (isBefore(aptDate, todayInTz)) {
          // Прошедшие занятия пропускаем
        } else {
          grouped.nextMonth.push(apt);
        }
      });

    return grouped;
  }, [appointments, profile?.timezone]);

  const now = new Date();
  const todayFormatted = format(now, 'd MMMM', { locale: ru });
  const nextMonthName = format(
    new TZDate(now.getFullYear(), now.getMonth() + 1, 1, profile?.timezone),
    'LLLL',
    { locale: ru }
  );

  const handleViewAllSchedule = () => {
    openScheduleModal({ isTeacher });
  };

  const renderLessonItem = (apt, index, totalInSection, isLastSection) => {
    const aptDate = new TZDate(apt.starts_at, profile?.timezone);
    const endDate = apt.ends_at ? new TZDate(apt.ends_at, profile?.timezone) : null;
    const hasHomeworkFlag = isTeacher ? apt.has_unreviewed_homework : apt.has_new_homework;

    return (
      <LessonItem
        key={apt.id || apt.starts_at}
        appointment={apt}
        date={aptDate}
        endDate={endDate}
        isHighlighted={hasHomeworkFlag}
        isLast={index === totalInSection - 1 && !isLastSection}
        isTeacher={isTeacher}
      />
    );
  };

  return (
    <div className={classNames(s.mobileClassBoard, className)}>
			<EditScheduleModal/>
      {/* Header с dropdown */}
      <div className={s.header}>
        {/* Badge домашнего задания */}
        {hasHomework && (
          <div className={s.homeworkBadge}>
            <span className={s.homeworkDot} />
            <span>Домашнее задание</span>
          </div>
        )}
      </div>

      {/* Заголовок "Сегодня" */}
      <div className={s.todayHeader}>
        <span>Сегодня,</span>
        <span className={s.todayDate}>{todayFormatted}</span>
				<div className={s.todayHeaderRight}>
					<TimezoneButton/>
				</div>
			</div>

      {/* Контент */}
      <div className={s.content}>
        {/* Занятия сегодня */}
        {groupedAppointments.today.length > 0 ? (
          <div className={s.lessonsList}>
            {groupedAppointments.today.map((apt, index) =>
              renderLessonItem(apt, index, groupedAppointments.today.length, !groupedAppointments.laterThisMonth.length)
            )}
          </div>
        ) : (
          <div className={s.noLessonsToday}>
            <div className={s.noLessonsDate}>
              <span className={s.dayNumber}>{new Date().getDate()}</span>
              <span className={s.dayName}>
                {['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][new Date().getDay()]}
              </span>
            </div>
            <div className={s.timelineLine} />
            <div className={s.noLessonsInfo}>
              <span className={s.noLessonsText}>Занятий нет</span>
            </div>
          </div>
        )}

        {/* Занятия далее в этом месяце */}
        {groupedAppointments.laterThisMonth.length > 0 && (
          <>
            <div className={s.sectionHeader}>
              <span>Далее в этом месяце</span>
            </div>
            <div className={s.lessonsList}>
              {groupedAppointments.laterThisMonth.map((apt, index) =>
                renderLessonItem(apt, index, groupedAppointments.laterThisMonth.length, !groupedAppointments.nextMonth.length)
              )}
            </div>
          </>
        )}

        {/* Занятия в следующем месяце */}
        {groupedAppointments.nextMonth.length > 0 && (
          <>
            <div className={s.sectionHeader}>
              <span>{nextMonthName.charAt(0).toUpperCase() + nextMonthName.slice(1)}</span>
            </div>
            <div className={s.lessonsList}>
              {groupedAppointments.nextMonth.map((apt, index) =>
                renderLessonItem(apt, index, groupedAppointments.nextMonth.length, true)
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer с кнопкой */}
      <div className={s.footer}>
        <button className={s.viewAllButton} onClick={handleViewAllSchedule}>
          Посмотреть все расписание
        </button>
      </div>
    </div>
  );
};
